/**
 * `tour/SpotlightTour.tsx` — the first-visit spotlight tour (#2048).
 *
 * A fixed layer dims the page except a rounded cut-out around the current
 * stop's `data-tour` anchor; a small card beside it says what the thing is
 * (title, one sentence, "Step n of N", Back / Next / Done, Skip). Six
 * stops (see `tourSteps.ts`); a stop whose anchor is missing is skipped.
 *
 * Shows by itself once per account on a lead's team home (`useTour.ts`),
 * and again from the top bar's Help menu ("Show me around"). Escape closes; focus
 * stays in the card while it is open and returns where it was after.
 *
 * The cut-out is one rounded box with a page-sized shadow in the dim colour
 * (so it moves smoothly between stops); a transparent layer under it keeps
 * the page from taking clicks while the tour is up. No external library.
 */
import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";

import type { WorkspaceRole } from "../../lib/activeWorkspace";
import { Button } from "../ui";
import { SpotlightFrame } from "./SpotlightFrame";
import { type ResolvedTourStop, findAnchor, resolveTourStops } from "./tourSteps";
import { isTourLeadRole, useTourAutoShow, useTourOpenRequests } from "./useTour";

export interface SpotlightTourProps {
  accountId: string | null | undefined;
  /** The account's role on the active team (leads and co-leads only). */
  workspaceRole: WorkspaceRole | null | undefined;
  /** Test seam: replaces the "page has settled" check of the auto-show. */
  isSettled?: (() => boolean) | undefined;
  /** Test seam: replaces the "anchor is on screen" check. */
  isShown?: ((el: Element) => boolean) | undefined;
}

export function SpotlightTour({
  accountId,
  workspaceRole: role,
  isSettled,
  isShown,
}: SpotlightTourProps) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [stops, setStops] = useState<ResolvedTourStop[]>([]);
  const [index, setIndex] = useState(0);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const resolve = useCallback(() => resolveTourStops(document, isShown), [isShown]);

  const start = useCallback(() => {
    const found = resolve();
    if (found.length === 0) return;
    returnFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setStops(found);
    setIndex(0);
    setOpen(true);
  }, [resolve]);

  const close = useCallback(() => {
    setOpen(false);
    const back = returnFocusRef.current;
    returnFocusRef.current = null;
    if (back?.isConnected) back.focus();
  }, []);

  useTourAutoShow({ accountId, role, pathname: location.pathname, onShow: start, isSettled });
  // "Show me around" — let the Help menu finish closing (it hands focus
  // back to its trigger) before the card takes focus.
  useTourOpenRequests(() => {
    if (!isTourLeadRole(role)) return;
    window.setTimeout(start, 60);
  });

  // Moving between stops re-reads the anchors, so a stop whose anchor
  // arrived late (the first-roster card loads after the shell) still shows.
  const go = useCallback(
    (delta: 1 | -1) => {
      const currentId = stops[index]?.id;
      const fresh = resolve();
      const at = fresh.findIndex((s) => s.id === currentId);
      const next = (at === -1 ? index : at) + delta;
      if (next >= fresh.length) {
        close();
        return;
      }
      setStops(fresh);
      setIndex(Math.max(0, next));
    },
    [stops, index, resolve, close],
  );

  if (!open || stops.length === 0) return null;
  const stop = stops[Math.min(index, stops.length - 1)];
  if (!stop) return null;
  return createPortal(
    <TourLayer
      stop={stop}
      step={index + 1}
      total={stops.length}
      onBack={() => go(-1)}
      onNext={() => go(1)}
      onClose={close}
    />,
    document.body,
  );
}

function TourLayer({
  stop,
  step,
  total,
  onBack,
  onNext,
  onClose,
}: {
  stop: ResolvedTourStop;
  step: number;
  total: number;
  onBack: () => void;
  onNext: () => void;
  onClose: () => void;
}) {
  const primaryRef = useRef<HTMLButtonElement>(null);
  const last = step === total;

  const target = useCallback(
    () => (stop.element.isConnected ? stop.element : findAnchor(stop.anchor)),
    [stop],
  );

  return (
    <SpotlightFrame
      stepKey={stop.id}
      getElement={target}
      onEscape={onClose}
      primaryRef={primaryRef}
    >
      {({ titleId, bodyId }) => (
        <>
          <p className="text-xs text-fg-subtle" data-testid="spotlight-tour-count">
            Step {step} of {total}
          </p>
          <h2 id={titleId} className="mt-1 text-base font-semibold tracking-tight">
            {stop.title}
          </h2>
          <p id={bodyId} className="mt-1 text-sm text-fg-muted">
            {stop.body}
          </p>
          <div className="mt-4 flex items-center justify-between gap-2">
            {last ? (
              <span />
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="rounded-sm text-xs text-fg-subtle underline-offset-4 hover:text-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                data-testid="spotlight-tour-skip"
              >
                Skip
              </button>
            )}
            <div className="flex items-center gap-2">
              {step > 1 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onBack}
                  data-testid="spotlight-tour-back"
                >
                  Back
                </Button>
              ) : null}
              <Button
                ref={primaryRef}
                size="sm"
                onClick={last ? onClose : onNext}
                data-testid={last ? "spotlight-tour-done" : "spotlight-tour-next"}
              >
                {last ? "Done" : "Next"}
              </Button>
            </div>
          </div>
        </>
      )}
    </SpotlightFrame>
  );
}
